import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Label, Spinner, Stack, StackItem, Title } from '@patternfly/react-core';
import { ExpandableRowContent, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';

import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

import { FlagEditor } from './FlagEditor';
import { Group } from './parseGroups';
import { ClusterRule, Fetcher, FlagInfo } from './types';

const muted: React.CSSProperties = { opacity: 0.7 };

/** Rules in effect: the saved ones, or the environment defaults while nothing is saved. */
const effectiveRules = (flag: FlagInfo): ClusterRule[] =>
  Array.isArray(flag.saved?.jsonValue) ? (flag.saved!.jsonValue as ClusterRule[]) : flag.envDefault.clusterRules ?? [];

const StatusCell: React.FC<{ flag: FlagInfo }> = ({ flag }) => {
  if (flag.valueOnly) {
    return flag.effective.stringValue ? <Label color="green">Set</Label> : <Label>Not set</Label>;
  }
  if (flag.hasClusterRules) {
    const active = effectiveRules(flag).filter(r => r.enabled !== false);
    return flag.effective.enabled ? (
      <Label color="green">{`On · ${active.length} rule${active.length === 1 ? '' : 's'}`}</Label>
    ) : (
      <Label>Off</Label>
    );
  }
  return flag.effective.enabled ? <Label color="green">On</Label> : <Label>Off</Label>;
};

const appliesTo = (flag: FlagInfo): string => {
  if (flag.valueOnly) return 'Everyone';
  if (flag.hasClusterRules) {
    const clusters = [...new Set(effectiveRules(flag).filter(r => r.enabled !== false).map(r => r.cluster))];
    return clusters.length > 0 ? `Groups limited to ${clusters.join(', ')}` : 'No one';
  }
  if (flag.supportsAudience && flag.saved?.audienceMode === 'groups') {
    const n = flag.saved.audienceGroups.length;
    return `${n} AD group${n === 1 ? '' : 's'}`;
  }
  return 'All users';
};

const AppConfigsPage: React.FunctionComponent = () => {
  const getToken = useAccessToken();
  // Keep `api` referentially stable so effects that depend on it don't re-run (and cancel debounced searches) on every render.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const api: Fetcher = useCallback((url, options) => fetchApi(url, () => getTokenRef.current(), options), []);
  const [flags, setFlags] = useState<FlagInfo[] | null>(null);
  const [clusters, setClusters] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<Group[]>([]);
  const [mine, setMine] = useState<Group[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [notices, setNotices] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ flags: FlagInfo[]; clusters: string[] }>('/api/app-config/flags');
        if (cancelled) return;
        setFlags(res.flags);
        setClusters(res.clusters);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load app configs');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, version]);

  useEffect(() => {
    api<{ groups: Group[] }>('/api/app-config/group-suggestions')
      .then(r => setSuggestions(r.groups ?? []))
      .catch(() => setSuggestions([]));
    api<{ groups: Group[] }>('/api/app-config/my-groups')
      .then(r => setMine(r.groups ?? []))
      .catch(() => setMine([]));
  }, [api]);

  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Stack hasGutter>
      <StackItem>
        <Title headingLevel="h1">App Configs</Title>
        <p>
          Turn features on or off and choose which users they affect, without a redeploy. Expand a row to edit it. Settings saved here override
          the environment defaults; &quot;Reset to default&quot; returns a feature to its environment value. Every change is audit-logged.
        </p>
      </StackItem>
      {error && (
        <StackItem>
          <Alert variant="danger" isInline title={error} />
        </StackItem>
      )}
      {!flags && !error && (
        <StackItem>
          <Spinner size="lg" aria-label="Loading app configs" />
        </StackItem>
      )}
      {flags && (
        <StackItem>
          <Table aria-label="App configs" variant="compact">
            <Thead>
              <Tr>
                <Th screenReaderText="Expand row" />
                <Th>Feature</Th>
                <Th>Status</Th>
                <Th>Applies to</Th>
                <Th>Source</Th>
                <Th>Last changed</Th>
              </Tr>
            </Thead>
            {flags.map((flag, rowIndex) => {
              const isOpen = expanded.has(flag.key);
              return (
                // Rebuild a row's editor only when the saved server data changes (save, reset). Keying on a counter instead
                // rebuilds before the refreshed data arrives, so the editor would start from the old values.
                <Tbody key={`${flag.key}-${flag.saved?.updatedAt ?? 'default'}`} isExpanded={isOpen}>
                  <Tr>
                    <Td expand={{ rowIndex, isExpanded: isOpen, onToggle: () => toggle(flag.key), expandId: `expand-${flag.key}` }} />
                    <Td dataLabel="Feature" modifier="breakWord">
                      <strong>{flag.label}</strong>
                      <div style={muted}>{flag.description}</div>
                    </Td>
                    <Td dataLabel="Status">
                      <StatusCell flag={flag} />
                    </Td>
                    <Td dataLabel="Applies to">{appliesTo(flag)}</Td>
                    <Td dataLabel="Source">{flag.saved ? <Label color="blue">Saved</Label> : <Label>Env default</Label>}</Td>
                    <Td dataLabel="Last changed">
                      {flag.saved ? (
                        <>
                          {flag.saved.updatedBy ?? 'unknown'}
                          <div style={muted}>{new Date(flag.saved.updatedAt).toLocaleString()}</div>
                        </>
                      ) : (
                        '—'
                      )}
                    </Td>
                  </Tr>
                  <Tr isExpanded={isOpen}>
                    <Td />
                    <Td colSpan={5}>
                      <ExpandableRowContent>
                        <FlagEditor
                          flag={flag}
                          clusters={clusters}
                          suggestions={suggestions}
                          mine={mine}
                          api={api}
                          notice={notices[flag.key]}
                          onChanged={text => {
                            setNotices(n => ({ ...n, [flag.key]: text }));
                            setVersion(v => v + 1);
                          }}
                        />
                      </ExpandableRowContent>
                    </Td>
                  </Tr>
                </Tbody>
              );
            })}
          </Table>
        </StackItem>
      )}
    </Stack>
  );
};

export { AppConfigsPage };
